import { useState, useCallback } from "react";
import { Link } from "@corpuscomplete/web/components/link";
import { Route } from "@corpuscomplete/web/routes/_admin/admin/ai-issues/$id";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Page, PageBody, PageHeader, PageContent } from "@corpuscomplete/web/components/layout";
import { Text } from "@corpuscomplete/web-ui/components/text";
import { Button } from "@corpuscomplete/web-ui/components/button";
import { Badge } from "@corpuscomplete/web-ui/components/badge";
import { Textarea } from "@corpuscomplete/web-ui/components/textarea";
import { Label } from "@corpuscomplete/web-ui/components/label";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from "@corpuscomplete/web-ui/components/dropdown-menu";
import { adminRpc } from "@corpuscomplete/web/rpc/client";
import { ChevronLeftIcon, ChevronDownIcon, CheckIcon, XIcon } from "lucide-react";
import type { AIIssue } from "@corpuscomplete/core/ai/ai-issue";

export function AIIssueDetailPage() {
  const { id } = Route.useParams();
  const queryClient = useQueryClient();

  const [reviewNotes, setReviewNotes] = useState("");
  const [rootCause, setRootCause] = useState<AIIssue.RootCause | undefined>(undefined);
  const [isEditing, setIsEditing] = useState(false);

  const {
    data: issue,
    isLoading,
    error,
  } = useQuery(adminRpc.aiIssues.byId.queryOptions({ input: { id } }));

  const updateStatusMutation = useMutation(
    adminRpc.aiIssues.updateStatus.mutationOptions({
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: adminRpc.aiIssues.key() });
        setIsEditing(false);
      },
    }),
  );

  const handleMarkNotable = useCallback(() => {
    updateStatusMutation.mutate({
      id,
      status: "notable",
      rootCause: rootCause,
      reviewNotes: reviewNotes.trim() || undefined,
    });
  }, [updateStatusMutation, id, rootCause, reviewNotes]);

  const handleDismiss = useCallback(() => {
    updateStatusMutation.mutate({
      id,
      status: "dismissed",
      rootCause: rootCause,
      reviewNotes: reviewNotes.trim() || undefined,
    });
  }, [updateStatusMutation, id, rootCause, reviewNotes]);

  const handleStartEdit = useCallback(() => {
    if (issue) {
      setRootCause(issue.rootCause ?? undefined);
      setReviewNotes(issue.reviewNotes ?? "");
    }
    setIsEditing(true);
  }, [issue]);

  if (isLoading) {
    return (
      <Page>
        <PageBody>
          <PageHeader>
            <Text variant="h5">Loading...</Text>
          </PageHeader>
        </PageBody>
      </Page>
    );
  }

  if (error || !issue) {
    return (
      <Page>
        <PageBody>
          <PageHeader>
            <Text variant="h5">Issue Not Found</Text>
          </PageHeader>
          <PageContent className="p-4">
            <div className="py-8 text-center">
              <Text variant="sm" className="mb-4 text-muted-foreground">
                {error?.message || "The requested issue could not be found."}
              </Text>
              <Link to="/admin/ai-issues">
                <Button variant="outline">Back to AI Issues</Button>
              </Link>
            </div>
          </PageContent>
        </PageBody>
      </Page>
    );
  }

  const createdDate = issue.timeCreated.toLocaleString("en-US", {
    dateStyle: "long",
    timeStyle: "medium",
  });

  const updatedDate = issue.timeUpdated.toLocaleString("en-US", {
    dateStyle: "long",
    timeStyle: "medium",
  });

  const isPending = issue.status === "pending";
  const canUpdate = isPending || isEditing;

  return (
    <Page>
      <PageBody>
        <PageHeader>
          <Link to="/admin/ai-issues">
            <Button variant="ghost" size="icon-xs">
              <ChevronLeftIcon className="size-4" />
            </Button>
          </Link>
          <Text variant="h5">AI Issue Details</Text>
        </PageHeader>
        <PageContent className="p-4">
          <div className="mx-auto flex max-w-3xl flex-col gap-6">
            <div className="flex items-start justify-between">
              <div className="flex flex-col gap-1">
                <div className="flex items-center gap-2">
                  <StatusBadge status={issue.status} />
                  <Badge variant="outline">{issue.type}</Badge>
                  {issue.rootCause && <RootCauseBadge rootCause={issue.rootCause} />}
                </div>
                <Text variant="sm" className="text-muted-foreground">
                  ID: {issue.id}
                </Text>
              </div>
              {!isPending && !isEditing && (
                <Button variant="outline" size="sm" onClick={handleStartEdit}>
                  Edit Status
                </Button>
              )}
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div className="flex flex-col gap-1">
                <Text variant="xs" className="font-medium text-muted-foreground">
                  Created
                </Text>
                <Text variant="sm">{createdDate}</Text>
              </div>
              <div className="flex flex-col gap-1">
                <Text variant="xs" className="font-medium text-muted-foreground">
                  Updated
                </Text>
                <Text variant="sm">{updatedDate}</Text>
              </div>
            </div>

            {issue.reason && (
              <div className="flex flex-col gap-2">
                <Text variant="xs" className="font-medium text-muted-foreground">
                  User Reason
                </Text>
                <div className="rounded-md border bg-muted/30 p-3">
                  <Text variant="sm">{issue.reason}</Text>
                </div>
              </div>
            )}

            <div className="flex flex-col gap-2">
              <Text variant="xs" className="font-medium text-muted-foreground">
                Context Data
              </Text>
              <div className="overflow-x-auto rounded-md border bg-muted/30 p-3">
                <pre className="text-xs">{JSON.stringify(issue.context, null, 2)}</pre>
              </div>
            </div>

            {issue.reviewNotes && !canUpdate && (
              <div className="flex flex-col gap-2">
                <Text variant="xs" className="font-medium text-muted-foreground">
                  Review Notes
                </Text>
                <div className="rounded-md border bg-muted/30 p-3">
                  <Text variant="sm">{issue.reviewNotes}</Text>
                </div>
              </div>
            )}

            {canUpdate && (
              <div className="flex flex-col gap-4 rounded-md border p-4">
                <Text variant="sm" className="font-medium">
                  {isEditing ? "Update Status" : "Review this Issue"}
                </Text>

                <div className="flex flex-col gap-2">
                  <Label>Root Cause (optional)</Label>
                  <DropdownMenu>
                    <DropdownMenuTrigger
                      render={
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={updateStatusMutation.isPending}
                        >
                          {rootCause ? formatRootCause(rootCause) : "Select root cause..."}
                          <ChevronDownIcon className="ml-1 size-3" />
                        </Button>
                      }
                    />
                    <DropdownMenuContent>
                      <DropdownMenuRadioGroup
                        value={rootCause ?? ""}
                        onValueChange={(v) => setRootCause((v as AIIssue.RootCause) || undefined)}
                      >
                        <DropdownMenuRadioItem value="">None</DropdownMenuRadioItem>
                        <DropdownMenuRadioItem value="data-gap">Data Gap</DropdownMenuRadioItem>
                        <DropdownMenuRadioItem value="search-issue">
                          Search Issue
                        </DropdownMenuRadioItem>
                        <DropdownMenuRadioItem value="model-issue">
                          Model Issue
                        </DropdownMenuRadioItem>
                      </DropdownMenuRadioGroup>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>

                <div className="flex flex-col gap-2">
                  <Label htmlFor="reviewNotes">Review Notes (optional)</Label>
                  <Textarea
                    id="reviewNotes"
                    placeholder="Add notes about this issue..."
                    value={reviewNotes}
                    onChange={(e) => setReviewNotes(e.target.value)}
                    disabled={updateStatusMutation.isPending}
                    maxLength={5000}
                  />
                </div>

                {updateStatusMutation.error && (
                  <Text variant="xs" className="text-destructive">
                    {updateStatusMutation.error.message}
                  </Text>
                )}

                <div className="flex items-center gap-2">
                  <Button onClick={handleMarkNotable} disabled={updateStatusMutation.isPending}>
                    <CheckIcon className="mr-1 size-4" />
                    Mark Notable
                  </Button>
                  <Button
                    variant="outline"
                    onClick={handleDismiss}
                    disabled={updateStatusMutation.isPending}
                  >
                    <XIcon className="mr-1 size-4" />
                    Dismiss
                  </Button>
                  {isEditing && (
                    <Button
                      variant="ghost"
                      onClick={() => setIsEditing(false)}
                      disabled={updateStatusMutation.isPending}
                    >
                      Cancel
                    </Button>
                  )}
                </div>
              </div>
            )}
          </div>
        </PageContent>
      </PageBody>
    </Page>
  );
}

function StatusBadge({ status }: { status: AIIssue.Status }) {
  switch (status) {
    case "pending":
      return <Badge variant="outline">Pending</Badge>;
    case "notable":
      return <Badge variant="default">Notable</Badge>;
    case "dismissed":
      return <Badge variant="secondary">Dismissed</Badge>;
  }
}

function RootCauseBadge({ rootCause }: { rootCause: AIIssue.RootCause }) {
  switch (rootCause) {
    case "data-gap":
      return <Badge variant="secondary">Data Gap</Badge>;
    case "search-issue":
      return <Badge variant="secondary">Search Issue</Badge>;
    case "model-issue":
      return <Badge variant="secondary">Model Issue</Badge>;
  }
}

function formatRootCause(rootCause: AIIssue.RootCause): string {
  switch (rootCause) {
    case "data-gap":
      return "Data Gap";
    case "search-issue":
      return "Search Issue";
    case "model-issue":
      return "Model Issue";
  }
}
